"use client";

import { ImageIcon } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

const SIZES = {
  md: { box: "size-11", icon: "size-5", sizes: "44px" },
  sm: { box: "size-10", icon: "size-4", sizes: "40px" },
} as const;

interface RecipeThumbProps {
  image: string | null;
  /** `md` is 44 px, `sm` is 40 px. */
  size?: keyof typeof SIZES;
}

/**
 * A recipe's small square photo: the photo, or the no-photo placeholder when there is none or the URL fails to load.
 * A plain `Image`, not `FadeImage`: the placeholder's tone sits behind the photo while it loads, and a fade driven by
 * the load event can leave a cached photo at zero opacity.
 */
export function RecipeThumb({ image, size = "md" }: RecipeThumbProps) {
  const [failed, setFailed] = useState(false);
  const src = failed ? null : image;
  const s = SIZES[size];

  return (
    <div className={`relative ${s.box} shrink-0 overflow-hidden rounded-lg bg-muted`}>
      {src ? (
        <Image
          src={src}
          alt=""
          fill
          sizes={s.sizes}
          className="object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
          <ImageIcon className={`${s.icon} opacity-30`} strokeWidth={1.5} aria-hidden />
        </div>
      )}
    </div>
  );
}
